import type { SignInWithPasswordCredentials } from '@supabase/supabase-js';
import { baseApi, getErrorMessage, setApiOrganizationId } from '@/shared/api';
import { Monitoring } from '@/shared/lib';
import { mapProfile } from '../libs/mapProfile';
import type { RequiredActionType } from '../model/types';
import type { ProfileDTO } from './types';

export const getProfile = async () => {
  const { data: sessionData, error: sessionError } =
    await baseApi.auth.getSession();

  if (sessionError) {
    Monitoring.captureError(sessionError);

    throw new Error(getErrorMessage(sessionError));
  }

  if (!sessionData.session) {
    throw new Error('User is not authorized');
  }

  const { data, error } = await baseApi.functions.invoke<ProfileDTO>(
    'profiles',
    {
      method: 'GET',
    },
  );

  if (!data) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
  return mapProfile(data);
};

export const getUserData = async () => {
  const { data: sessionData, error: sessionError } =
    await baseApi.auth.getSession();

  if (sessionError) {
    throw sessionError;
  }

  if (
    sessionData.session &&
    getRememberMe(sessionData.session.user.email || '')
  ) {
    return sessionData.session.user;
  }

  const { data: userData, error: userError } = await baseApi.auth.getUser();

  if (userError) {
    // skip throw when user unauthorized
    if (userError.status === 403) {
      return;
    }

    throw userError;
  }

  return userData.user;
};

export const signInWithGoogle = async () => {
  const { error } = await baseApi.auth.signInWithOAuth({
    provider: 'google',
  });

  if (error) {
    throw error;
  }
};

export const login = async (credentials: SignInWithPasswordCredentials) => {
  const { data, error } = await baseApi.auth.signInWithPassword(credentials);

  if (error) {
    throw new Error(error.message);
  }

  return data.user;
};

export const logout = async () => {
  const { error } = await baseApi.auth.signOut();

  if (error) {
    throw error;
  }
};

export const changePassword = async (newPassword: string) => {
  const { error } = await baseApi.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    throw error;
  }
};

export const deleteRequiredAction = async (
  type: RequiredActionType,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke(
    'profiles/required-actions',
    {
      method: 'DELETE',
      body: { type },
    },
  );

  if (error) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
};

export const updateLastActiveAt = async (): Promise<void> => {
  await baseApi.functions.invoke('profiles', {
    method: 'PATCH',
  });
};

export const updateDefaultOrganizationId = async (
  defaultOrganizationId: string,
): Promise<void> => {
  const { error } = await baseApi.functions.invoke('profiles', {
    body: { defaultOrganizationId },
    method: 'PUT',
  });

  if (error) {
    const errorMessage = getErrorMessage(error);

    throw new Error(errorMessage);
  }
};

export const requestResetPassword = async (email: string) => {
  const { error } = await baseApi.auth.resetPasswordForEmail(email);

  if (error) {
    throw error;
  }
};

const REMEMBER_ME_KEY = 'cw-remember-me';

const getRememberMe = (email: string): boolean => {
  const item = localStorage.getItem(REMEMBER_ME_KEY);

  return item === email;
};

export const updateRememberMe = (email: string) => {
  localStorage.setItem(REMEMBER_ME_KEY, email);
};

export const deleteRememberMe = () => {
  localStorage.removeItem(REMEMBER_ME_KEY);
};

const SHOW_PAYMENT_OVERDUE_BANNER_KEY = 'cw-show-payment-overdue-banner';

export const getShowPaymentOverdueBanner = (): boolean => {
  return localStorage.getItem(SHOW_PAYMENT_OVERDUE_BANNER_KEY) === 'true';
};

export const setShowPaymentOverdueBanner = (
  showPaymentOverdueBanner: boolean,
) => {
  const currentShowPaymentOverdueBannerValue = localStorage.getItem(
    SHOW_PAYMENT_OVERDUE_BANNER_KEY,
  );

  if (
    !showPaymentOverdueBanner ||
    currentShowPaymentOverdueBannerValue === 'false'
  ) {
    return;
  }

  localStorage.setItem(SHOW_PAYMENT_OVERDUE_BANNER_KEY, 'true');
};

export const hidePaymentOverdueBanner = () => {
  localStorage.setItem(SHOW_PAYMENT_OVERDUE_BANNER_KEY, 'false');
};

export const deleteShowPaymentOverdueBanner = () => {
  localStorage.removeItem(SHOW_PAYMENT_OVERDUE_BANNER_KEY);
};

const ACTIVE_ORGANIZATION_ID_KEY = 'cw-active-organization-id';

export const getActiveOrganizationId = (): string | null => {
  return sessionStorage.getItem(ACTIVE_ORGANIZATION_ID_KEY);
};

export const setActiveOrganizationId = (organizationId: string) => {
  sessionStorage.setItem(ACTIVE_ORGANIZATION_ID_KEY, organizationId);
  setApiOrganizationId(organizationId);
};

export const deleteActiveOrganizationId = () => {
  sessionStorage.removeItem(ACTIVE_ORGANIZATION_ID_KEY);
  setApiOrganizationId(undefined);
};
